import { PLAYER } from "./config.ts";
import type { Pose, Vec3 } from "./math.ts";
import type { LadderSpec } from "./ship.ts";
import { COLLIDERS, LANDMASSES, anchorPlacement, placementPoint, propPlacement, terrainHeight, type Collider, type Placement, type PropModel } from "./world.ts";

/**
 * Interactive island fixtures derived from shared world data: tower ladders, gates and
 * their levers, loot crates and mounted telescopes. Server authority and client
 * presentation both read these lists; replicated state indexes them by position.
 */

/** Model-local ladder geometry (author frame: x, y = forward, z = up) from tools/models/bastion.py. */
const LADDER_MODELS: Partial<Record<PropModel, { x: number; forward: number; lean: number; z0: number; z1: number; deck: number; inset: number }>> = {
  beaconTower: { x: 0, forward: -2.28, lean: .6, z0: .12, z1: 16, deck: 16, inset: 1 },
  scaffoldTower: { x: 0, forward: -1.68, lean: .55, z0: .1, z1: 7, deck: 7, inset: .8 },
  seaLookout: { x: -2.12, forward: -2.12, lean: -.6, z0: 2.6, z1: 11.4, deck: 11.5, inset: .75 },
};

/** Climber clearance from the rung plane; the capsule radius plus a hand's reach. */
const LADDER_OUT = .4;
const RUNG_SPACING = .38;
/** Vertical climbing speed on rungs, metres per second. */
const CLIMB_SPEED = 2;

export interface WorldLadder {
  /** Ladder frame on the world plane: +x points out of the tower face toward the climber. */
  frame: Pose;
  /** World height of the frame origin (the model's base). */
  y: number;
  spec: LadderSpec;
}

export interface Gate {
  at: Placement;
}

export interface GateLever {
  gate: number;
  at: Placement;
}

export interface LootCrate {
  at: Placement;
}

export interface Telescope {
  at: Placement;
  /** Where the observer stands, behind the eyepiece, and which way they face. */
  stand: Vec3;
  yaw: number;
}

function towerLadder(model: PropModel, at: Placement): WorldLadder | null {
  const m = LADDER_MODELS[model];
  if (!m) return null;
  // The ladder frame's +x is the model's -forward (out of the tower face); its origin lies on the top rung line.
  const origin = placementPoint(at, m.x, 0, m.forward), heading = at.yaw + Math.PI / 2;
  // Rungs lean `lean` metres inward at their foot; the climber hangs LADDER_OUT outside them.
  const rungX = (y: number) => -m.lean * (1 - (y - m.z0) / (m.z1 - m.z0));
  const foot = placementPoint({ x: origin.x, y: at.y, z: origin.z, yaw: heading }, rungX(m.z0) + LADDER_OUT, 0, 0);
  const bottomY = Math.max(Math.max(terrainHeight(foot.x, foot.z), PLAYER.surfaceFeetY) - at.y, m.z0 - .6);
  const step = (m.z1 - m.z0) / Math.max(1, Math.floor((m.z1 - m.z0) / RUNG_SPACING));
  const head = { x: LADDER_OUT, y: m.deck + .2, z: 0 };
  return {
    frame: { x: origin.x, z: origin.z, heading },
    y: at.y,
    spec: {
      bottom: { x: rungX(bottomY) + LADDER_OUT, y: bottomY, z: 0 },
      head,
      top: { x: -m.inset, y: m.deck, z: 0 },
      rungBase: m.z0 + step / 2,
      rungStep: step,
      rungOut: LADDER_OUT,
      crest: { x: -.12, y: m.deck + .06, z: 0 },
      crestSpread: .3,
      seconds: Math.max(2.2, (head.y - bottomY) / CLIMB_SPEED / .75),
    },
  };
}

const ladders: WorldLadder[] = [], gates: Gate[] = [], levers: GateLever[] = [], crates: LootCrate[] = [], telescopes: Telescope[] = [];
for (const l of LANDMASSES) {
  const gateIds: string[] = [];
  for (const m of l.landmarks) {
    const at = anchorPlacement(l, m.id);
    const ladder = towerLadder(m.kind as PropModel, at);
    if (ladder) ladders.push(ladder);
    if (m.kind === "stockadeGate") { gates.push({ at }); gateIds.push(m.id); }
  }
  for (const p of l.props ?? []) {
    const at = propPlacement(l, p);
    const ladder = towerLadder(p.model, at);
    if (ladder) ladders.push(ladder);
    if (p.model === "lootCrate") crates.push({ at });
    if (p.model === "mountedTelescope") {
      // Tube points along model +z; the observer stands 0.8 m behind the eyepiece.
      telescopes.push({ at, stand: placementPoint(at, 0, 0, -.85), yaw: at.yaw });
    }
    if (p.model === "gateLever") {
      const index = p.on ? gateIds.indexOf(p.on) : -1;
      if (index < 0) throw new Error(`gate lever on ${l.id} must be mounted on a stockade gate`);
      levers.push({ gate: gates.length - gateIds.length + index, at });
    }
  }
}

export const WORLD_LADDERS: readonly WorldLadder[] = ladders;
export const GATES: readonly Gate[] = gates;
export const GATE_LEVERS: readonly GateLever[] = levers;
export const LOOT_CRATES: readonly LootCrate[] = crates;
export const TELESCOPES: readonly Telescope[] = telescopes;

/** Swing of the gate door (model pivot `door`) into the yard when open, radians about vertical. */
export const GATE_OPEN_ANGLE = 95 * Math.PI / 180;

/** Ladder-frame point → world, including the frame's base height; `p` and `out` may alias. */
export function ladderPoint(ladder: WorldLadder, p: Vec3, out: Vec3): Vec3 {
  const c = Math.cos(ladder.frame.heading), s = Math.sin(ladder.frame.heading);
  const x = p.x, y = p.y, z = p.z;
  out.x = ladder.frame.x + x * c + z * s;
  out.y = ladder.y + y;
  out.z = ladder.frame.z - x * s + z * c;
  return out;
}

const scratch: Vec3 = { x: 0, y: 0, z: 0 };

/** Which end of a world ladder (if any) a world-frame body at feet (x, y, z) may grab. */
export function worldLadderEnd(ladder: WorldLadder, x: number, y: number, z: number, range: number): "bottom" | "top" | null {
  ladderPoint(ladder, ladder.spec.bottom, scratch);
  if (Math.hypot(x - scratch.x, z - scratch.z) <= range && Math.abs(y - scratch.y) <= 1.6) return "bottom";
  ladderPoint(ladder, ladder.spec.top, scratch);
  // The deck end is tight: decks also carry telescopes and crates within a couple of metres.
  if (Math.hypot(x - scratch.x, z - scratch.z) <= range * .5 && Math.abs(y - scratch.y) <= .6) return "top";
  return null;
}

/** Hinge of the door leaf (model pivot `door`), in gate-local metres. */
const GATE_HINGE_X = -1.78;

/**
 * The gate door as a row of posts along its leaf: across the passage, sealing it between the
 * gateposts, or swung back into the yard (Babylon yaw convention: local +x turns to -z).
 */
function gateDoor(g: Gate, open: boolean, out: Collider[]): void {
  const angle = open ? GATE_OPEN_ANGLE : 0, dx = Math.cos(angle), dz = -Math.sin(angle);
  for (let i = 0; i <= 6; i++) {
    const t = .28 + i * .5;
    const p = placementPoint(g.at, GATE_HINGE_X + dx * t, 0, dz * t);
    out.push({ kind: "cyl", x: p.x, z: p.z, r: open ? .15 : .3, y0: g.at.y - .5, y1: g.at.y + 4.25, blocksShip: false });
  }
}

/**
 * Static world colliders plus gate doors and intact crates. Callers cache the result and
 * rebuild it only when gate or crate state changes; never call per step.
 */
export function worldColliders(gateOpen: ArrayLike<boolean>, crateCooldown: ArrayLike<number>): Collider[] {
  const out = COLLIDERS.slice();
  GATES.forEach((g, i) => gateDoor(g, !!gateOpen[i], out));
  LOOT_CRATES.forEach((c, i) => {
    if ((crateCooldown[i] ?? 0) > 0) return;
    out.push({ kind: "cyl", x: c.at.x, z: c.at.z, r: .45, y0: c.at.y - .2, y1: c.at.y + .78, blocksShip: false });
  });
  return out;
}
